import React, { useState, useRef, useMemo, useEffect } from 'react'

import { useMutation } from '@tanstack/react-query'
import { MIXPANEL_EVENTS } from 'lib/mixpanel/events'
import { trackMixpanelEvent } from 'lib/mixpanel/track'
import { X } from 'phosphor-react'
import { Typeahead, asyncContainer } from 'react-bootstrap-typeahead'
import PlacesAutocomplete, {
	geocodeByAddress,
	getLatLng
} from 'react-places-autocomplete'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'

import { collapse, updateFilter } from 'redux/modules/filters'

import { autoSearch } from 'services/psychologists'

import { useUser } from 'providers/UserProvider'

import { Button } from 'components/v2/Button'
import { Checkbox } from 'components/v2/Checkbox'
import { Row, Column } from 'components/v2/Grid'
import { Camera, Close, Location } from 'components/v2/Icons/assets'
import { Flag } from 'components/v2/Icons/assets/Flag'
import { MaskedInput } from 'components/v2/Input'
import TypeaheadSelect from 'components/v2/MoreFilters/TypeheadSelect'
import { RangeSlider } from 'components/v2/RangeSlider'
import { Text } from 'components/v2/Text'
import { MultiSelectInputField } from 'components/v3/FormFields/MultiSelectInputField/MultiSelectInputField'

import { checkIsMobile } from 'utils/helpers'

const AsyncTypeahead = asyncContainer(Typeahead)

const _AsyncTypeahead = React.forwardRef((props, ref) => {
	return <AsyncTypeahead ref={ref} {...props} />
})
_AsyncTypeahead.displayName = '_AsyncTypeahead'

const isMobile = checkIsMobile(993)

const MoreFilters = ({ onSearchPsychologists = () => {} }) => {
	const [hasTypedAnInvalidAddress, setHasTypedAnInvalidAddress] =
		useState(false)
	const [searchedPsychologistsByName, setSearchedPsychologistsByName] =
		useState([])
	const [hasTouchedMinPrice, setHasTouchedMinPrice] = useState(false)
	const [hasTouchedMaxPrice, setHasTouchedMaxPrice] = useState(false)
	const nameRef = useRef()
	const dispatch = useDispatch()
	const { shouldHideConsultationValue, isPresencialConsultationEnabled } =
		useUser()
	const { filters, skills } = useSelector((state) => ({
		filters: state.filters,
		skills: state.skills
	}))

	const isOnlineModality = useMemo(
		() => filters?.consultation === 'online',
		[filters]
	)

	const onToggleMoreFiltersVisibility = () => dispatch(collapse())

	const onPsychologistChange = ([name]) =>
		dispatch(updateFilter({ nameProfessional: name }))

	const { mutate: onSearchPsychologist, isLoading } = useMutation({
		mutationFn: ({ nameProfessional }) => autoSearch({ nameProfessional }),
		onSuccess: ({ data: { nameProfessional } }) => {
			setSearchedPsychologistsByName(nameProfessional)
		},
		onError: (error) => {
			console.log(error)
		}
	})

	const onManuallyChangeConsultationPriceRange = (range, value) => {
		const number = Number(value)
		const payload = {
			min: { min_price: number },
			max: { max_price: number }
		}
		dispatch(updateFilter(payload[range]))
	}

	const onValidateConsultationPriceRange = (value) => {
		if (filters?.min_price > filters?.max_price) {
			return 'O valor mínimo não pode ser maior que o valor máximo'
		}
		if (value < 50 || value > 350) {
			return 'O valor deve ser no mínimo 50 e no máximo 350'
		}
		return false
	}

	const onSelectSkill = (skills) => {
		if (skills?.length > 3) {
			return
		}
		return dispatch(updateFilter({ skills }))
	}

	const onAddressChange = async (address) => {
		dispatch(updateFilter({ address }))
	}

	const onConsultationModalityChange = (consultation) => {
		dispatch(updateFilter({ consultation }))
	}

	const onPsychologistGenderChange = (gender, isSelected) =>
		dispatch(updateFilter({ [gender]: isSelected }))

	const onConsultationTargetAudienceChange = (targetAudience, isSelected) =>
		dispatch(updateFilter({ [targetAudience]: isSelected }))

	const onChangeConsultationPriceRange = ([min_price, max_price]) =>
		dispatch(updateFilter({ min_price, max_price }))

	const onSelectLanguage = (language) =>
		dispatch(
			updateFilter({
				languages_code: filters?.languages_code?.includes(language)
					? filters?.languages_code?.filter((it) => it !== language)
					: [...(filters?.languages_code ?? []), language]
			})
		)

	function handleKeyDown(e) {
		const instance = nameRef.current.getInstance()
		e?.keyCode === 8 && filters?.nameProfessional && instance.clear()
	}

	useEffect(() => {
		;(async () => {
			await geocodeByAddress(filters?.address).then((results) =>
				getLatLng(results[0]).then((latLng) =>
					dispatch(
						updateFilter({
							lat: latLng.lat,
							long: latLng.lng
						})
					)
				)
			)
		})()
	}, [filters?.address])

	return (
		<BackgroundContainer isOpen={filters?.collapsed}>
			<CollapseContainer isOpen={filters?.collapsed} isMobile={isMobile}>
				<Row className='mb-[30px] max-h-[71px] w-full items-center justify-between border-b-2 border-solid border-gray-200 px-[18px] py-[22px] sm:px-[98px]'>
					<Text variant='regular' className='text-2xl md:text-[26px]'>
						Filtros
					</Text>
					<Close size='16px' onClick={onToggleMoreFiltersVisibility} />
				</Row>

				<Column className='px-[18px] sm:px-[98px]'>
					{isPresencialConsultationEnabled && (
						<Row className='mb-[15px]'>
							<StyledButton
								variant={
									isOnlineModality
										? 'primary-outlined-inactive'
										: 'primary-outlined-active'
								}
								onClick={() => onConsultationModalityChange('presencial')}
								type='button'
								className='mr-4 h-12 w-full'
							>
								<Row className='items-center justify-center'>
									<Location
										size='13px'
										color={isOnlineModality ? '#7F8C8D' : '#FF6A58'}
									/>
									<Text variant='small' className='ml-2'>
										Presencial
									</Text>
								</Row>
							</StyledButton>
							<StyledButton
								variant={
									isOnlineModality
										? 'primary-outlined-active'
										: 'primary-outlined-inactive'
								}
								onClick={() => onConsultationModalityChange('online')}
								type='button'
								className='h-12 w-full'
							>
								<Row className='items-center justify-center'>
									<Camera
										size='14px'
										color={isOnlineModality ? '#FF6A58' : '#7F8C8D'}
									/>
									<Text variant='small' className='ml-2'>
										Online
									</Text>
								</Row>
							</StyledButton>
						</Row>
					)}
					<Column className='mb-[15px]'>
						<TypeaheadSelect
							title='Procure por especialidade ou transtorno'
							titleColor='#FF7708'
							emptyLabel='Nenhuma sugestão encontrada'
							placeholder='Como Ansiedade, Depressão, TDAH'
							showMoreLabel='Mostrar mais resultados...'
						/>
					</Column>
					<Column className='relative mb-[30px] h-[52px] items-start rounded border border-solid border-gray-900 bg-white py-2'>
						<Text variant='tiny' className='px-3 text-left text-orange-600'>
							Nome do Psicólogo
						</Text>
						<StyledAsyncTypeahead
							id='professional'
							isLoading={isLoading}
							onSearch={onSearchPsychologist}
							onChange={onPsychologistChange}
							options={searchedPsychologistsByName}
							defaultInputValue={filters?.nameProfessional}
							placeholder='Sabe o nome do seu psicólogo? Digite aqui!'
							searchText='Carregando...'
							emptyLabel='Nenhum psicólogo encontrado.'
							promptText='Digite para buscar..'
							ref={nameRef}
							onKeyDown={handleKeyDown}
						/>
						{!!filters?.nameProfessional?.length && (
							<X
								size={20}
								color='#FF7708'
								onClick={() => nameRef.current.getInstance().clear()}
								style={{
									right: '0.5rem',
									top: '33%',
									position: 'absolute',
									cursor: 'pointer'
								}}
							/>
						)}
					</Column>
					{!isOnlineModality && (
						<Column className='mb-[30px]'>
							<Column
								className={`mb-0.5 h-[52px] items-start rounded border border-solid bg-white px-3 py-2 ${hasTypedAnInvalidAddress ? 'border-red-500' : 'border-gray-900'}`}
							>
								<Text variant='tiny' className='text-left text-orange-600'>
									Onde você está?
								</Text>
								<PlacesAutocomplete
									inputProps={{
										placeholder: 'CEP, bairro ou cidade',
										type: 'search',
										value: filters?.address,
										onChange: (address) => {
											onAddressChange(address)

											if (hasTypedAnInvalidAddress) {
												setHasTypedAnInvalidAddress(false)
											}
										}
									}}
									autocompleteItem={({ formattedSuggestion }) => (
										<Row className='flex-wrap items-center justify-start'>
											<Text variant='regular' className='mr-1 font-bold'>
												{formattedSuggestion.mainText}
											</Text>
											<Text variant='tiny'>
												{' '}
												{formattedSuggestion.secondaryText}
											</Text>
										</Row>
									)}
									styles={{
										autocompleteContainer: {
											display: 'flex',
											flexDirection: 'column',
											width: '100%',
											minWidth: '300px',
											borderColor: '#BDC3C7',
											top: 28,
											left: -12,
											zIndex: 3
										},
										input: {
											fontSize: '16px',
											minWidth: '260px',
											height: 'auto'
										}
									}}
									onError={() => setHasTypedAnInvalidAddress(true)}
									type='search'
									placeholder='CEP, bairro ou cidade'
									googleLogo={false}
									options={{
										componentRestrictions: { country: 'br' }
									}}
								/>
							</Column>
							{hasTypedAnInvalidAddress && (
								<Text
									variant='tiny'
									className='text-left text-[9px] text-red-500 md:text-[11px]'
								>
									Digite um endereço válido!
								</Text>
							)}
						</Column>
					)}
					<Row className='mb-[30px] border-b-2 border-solid border-gray-200' />
					{!shouldHideConsultationValue && (
						<Column>
							<Text variant='regular' className='mb-4 text-left'>
								Valor da consulta
							</Text>

							<RangeSlider
								range
								min={50}
								max={350}
								value={[filters?.min_price, filters?.max_price]}
								onChange={onChangeConsultationPriceRange}
								allowCross={false}
								pushable={9}
							/>

							<Row className='my-[30px] justify-center'>
								<MaskedInput
									className='mb-0 w-full'
									type='number'
									variant='primary'
									onChange={({ target }) => {
										setHasTouchedMinPrice(true)
										onManuallyChangeConsultationPriceRange('min', target.value)
									}}
									mask='no_mask'
									value={filters?.min_price}
									name='min_price'
									label='Mínimo*'
									placeholder='R$50'
									error={
										hasTouchedMinPrice
											? onValidateConsultationPriceRange(filters?.min_price)
											: null
									}
								/>
								<Column className='h-12 justify-center'>
									<Row className='mx-3 h-0.5 w-[9px] border-b-2 border-solid border-gray-200'></Row>
								</Column>
								<MaskedInput
									className='mb-0 w-full'
									type='number'
									variant='primary'
									onChange={({ target }) => {
										setHasTouchedMaxPrice(true)
										onManuallyChangeConsultationPriceRange('max', target.value)
									}}
									mask='no_mask'
									value={filters?.max_price}
									name='max_price'
									label='Máximo*'
									placeholder='R$350'
									error={
										hasTouchedMaxPrice
											? onValidateConsultationPriceRange(filters?.max_price)
											: null
									}
								/>
							</Row>
							<Row className='mb-[30px] border-b-2 border-solid border-gray-200' />
						</Column>
					)}
					<Text variant='regular' className='mb-4 text-left'>
						Abordagem
					</Text>
					<Row className='mb-[30px] justify-center'>
						<MultiSelectInputField
							variant='secondary'
							name='skills'
							placeholder='Ver todas'
							options={
								skills?.data.map((skill) => {
									return {
										label: skill?.title,
										value: skill?.id
									}
								}) || []
							}
							value={filters?.skills}
							onChange={(event) => onSelectSkill(event.target.value)}
						/>
					</Row>
					<Row className='mb-[30px] border-b-2 border-solid border-gray-200' />
					<Text variant='regular' className='mb-4 text-left'>
						Gênero do psicólogo
					</Text>
					<Row className='mb-[30px]'>
						<Checkbox
							name='female'
							label='Feminino'
							checked={filters?.female}
							onClick={() =>
								onPsychologistGenderChange('female', !filters?.female)
							}
						/>
						<Checkbox
							name='male'
							label='Masculino'
							checked={filters?.male}
							onClick={() => onPsychologistGenderChange('male', !filters?.male)}
						/>
					</Row>
					<Row className='mb-[30px] border-b-2 border-solid border-gray-200' />
					<Text variant='regular' className='mb-4 text-left'>
						Idiomas e acessibilidade
					</Text>
					<Row className='mb-[30px] h-auto w-full'>
						<Column className='w-1/2'>
							<Checkbox
								name='pt-br'
								label='Português'
								checked={filters?.languages_code?.includes('pt-br')}
								icon={<Flag variant='portuguese' />}
								onClick={() => onSelectLanguage('pt-br')}
							/>
							<Checkbox
								name='en-us'
								label='Inglês'
								checked={filters?.languages_code?.includes('en-us')}
								icon={<Flag variant='english' />}
								onClick={() => onSelectLanguage('en-us')}
							/>
							<Checkbox
								name='es-es'
								label='Espanhol'
								checked={filters?.languages_code?.includes('es-es')}
								icon={<Flag variant='spanish' />}
								onClick={() => onSelectLanguage('es-es')}
							/>
							<Checkbox
								name='it-ele'
								label='Italiano'
								checked={filters?.languages_code?.includes('it-ele')}
								icon={<Flag variant='italian' />}
								onClick={() => onSelectLanguage('it-ele')}
							/>
						</Column>
						<Column className='w-1/2'>
							<Checkbox
								name='fr-fr'
								label='Francês'
								checked={filters?.languages_code?.includes('fr-fr')}
								icon={<Flag variant='french' />}
								onClick={() => onSelectLanguage('fr-fr')}
							/>
							<Checkbox
								name='ja-ja'
								label='Japonês'
								checked={filters?.languages_code?.includes('ja-ja')}
								icon={<Flag variant='japanese' />}
								onClick={() => onSelectLanguage('ja-ja')}
							/>
							<Checkbox
								name='zh-cn'
								label='Chinês'
								checked={filters?.languages_code?.includes('zh-cn')}
								icon={<Flag variant='chinese' />}
								onClick={() => onSelectLanguage('zh-cn')}
							/>
							<Checkbox
								name='asgn-b'
								label='Libras'
								checked={filters?.languages_code?.includes('asgn-b')}
								icon={<Flag variant='signLanguage' />}
								onClick={() => onSelectLanguage('asgn-b')}
							/>
						</Column>
					</Row>
					<Row className='mb-[30px] border-b-2 border-solid border-gray-200' />
					<Text variant='regular' className='mb-4 text-left'>
						Atendimento
					</Text>
					<Row className='mb-[30px]'>
						<Column className='w-1/2'>
							<Checkbox
								name='adult'
								label='Adultos'
								checked={filters?.adult}
								onClick={() =>
									onConsultationTargetAudienceChange('adult', !filters?.adult)
								}
							/>
							<Checkbox
								name='adolescent'
								label='Adolescentes'
								checked={filters?.adolescent}
								onClick={() =>
									onConsultationTargetAudienceChange(
										'adolescent',
										!filters?.adolescent
									)
								}
							/>
							<Checkbox
								name='kids'
								label='Crianças'
								checked={filters?.kids}
								onClick={() =>
									onConsultationTargetAudienceChange('kids', !filters?.kids)
								}
							/>
						</Column>
						<Column className='w-1/2'>
							<Checkbox
								name='elderly'
								label='Idosos'
								checked={filters?.elderly}
								onClick={() =>
									onConsultationTargetAudienceChange(
										'elderly',
										!filters?.elderly
									)
								}
							/>
							<Checkbox
								name='couple'
								label='Casais'
								checked={filters?.couple}
								onClick={() =>
									onConsultationTargetAudienceChange('couple', !filters?.couple)
								}
							/>
						</Column>
					</Row>
				</Column>
				<Row className='sticky bottom-0 max-h-[71px] w-full items-center justify-between bg-white px-[18px] py-[15px] shadow-[0px_0px_12px_#00000012] sm:px-[98px]'>
					<Button
						type='submit'
						onClick={() => {
							trackMixpanelEvent(MIXPANEL_EVENTS.search.filter_apply_clicked)
							if (filters?.nameProfessional) {
								trackMixpanelEvent(
									MIXPANEL_EVENTS.search_by_name.search_submitted,
									{
										professional_name: String(filters.nameProfessional)
									}
								)
							}
							onToggleMoreFiltersVisibility()
							onSearchPsychologists()
						}}
						className='h-12 w-full'
					>
						<Text variant='small' className='font-bold'>
							Aplicar filtros
						</Text>
					</Button>
				</Row>
			</CollapseContainer>
		</BackgroundContainer>
	)
}

export default MoreFilters

const ImageIndex = styled.div`
	z-index: 2;
`

const fadeIn = () => keyframes`
  from {
    max-width: 0;
    min-width: 0;
    opacity: 0;
  }
  to {
    max-width: 534px;
    min-width: 534px;
    opacity: 1;
  }
`
const fadeInMobile = () => keyframes`
  from {
    max-width: 0;
    min-width: 0;
    opacity: 0;
  }
  to {
    max-width: 100%;
    min-width: 100%;
    opacity: 1;
  }
`

const BackgroundContainer = styled.div`
	${({ isOpen }) => {
		const background = `
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    position:fixed;
    display: flex;
    z-index: 9999;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    margin:0px;
    padding:0px;
    `

		if (isOpen) {
			return background
		}
	}}
`

const CollapseContainer = styled(Column)`
	overflow-y: auto;
	display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
	width: 100%;
	height: 100%;
	position: fixed;
	z-index: 99999;
	background: ${({ theme: { colors } }) => colors.white};
	top: 0;
	right: 0;
	animation: ${({ isMobile }) => (isMobile ? fadeInMobile : fadeIn)} 0.5s ease
		forwards;
	@media (min-width: 994px) {
		max-width: 534px;
	}
`
const StyledLink = styled.a`
	color: #acacac;
	font-weight: 900;
	text-decoration: underline;
	margin-left: 3px;

	&:hover {
		color: #000;
	}
`

const StyledTypeahead = styled(Typeahead)`
	div {
		height: auto;
	}
	.rbt-input {
		input {
			width: 268px !important;
			font-size: 16px !important;
			padding: 0 12px !important;
			@media (max-width: 768px) {
				width: 510px !important;
			}
			@media (max-width: 425px) {
				width: 320px !important;
			}
			@media (max-width: 375px) {
				width: 280px !important;
			}
			@media (max-width: 320px) {
				width: 225px !important;
			}
		}
	}
	.dropdown-menu {
		margin: 3px 0 0 0 !important;
		width: 320px !important;
	}
`

const StyledAsyncTypeahead = styled(_AsyncTypeahead)`
	div {
		height: auto;
	}
	.rbt-input {
		input {
			width: 295px !important;
			font-size: 16px !important;
			padding: 0 12px !important;
			@media (max-width: 768px) {
				width: 510px !important;
			}
			@media (max-width: 425px) {
				width: 320px !important;
			}
			@media (max-width: 375px) {
				width: 280px !important;
			}
			@media (max-width: 320px) {
				width: 225px !important;
			}
		}
	}
	.dropdown-menu {
		margin: 3px 0 0 0 !important;
	}
	.rbt-input.form-control {
		border-top: none !important;
		border-top-left-radius: 0px !important;
		border-top-right-radius: 0px !important;
	}
`

const StyledButton = styled(Button)`
	:focus {
		outline: none;
	}
`
